import { isBanner, isCarousel } from '@/components/card';
import { ForwardFocusIcon, PlayFocusIcon } from '@/components/card/focus-icon';
import { CardVariant } from '@/components/card/types';
import { ImageDecorationProps } from '@/components/focusable-overlaid-image/image-decoration';
import { ImageDecorationPosition } from '@/components/focusable-overlaid-image/types';
import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_WIDTH } from '@calm/evexia-design-tokens';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import commonMessages from '@rhg/lang/messages/common';
import { logger } from '@rhg/libs/logger';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareSessionCardProps } from '..';
import { useSessionCardPlayHandler } from '../use-play-handler';
import { useVariantAgnosticSessionCardProps } from '../use-variant-agnostic-session-card-props';

const styleHorizontalCard = {
    width: { md: `${DS_WIDTH.FORM_MAX_WIDTH}px`, sm: '100%' },
};

const styleCarouselContent = {
    minHeight: DS_HEIGHT.CARD_MEDIUM,
    justifyContent: 'flex-start',
};

const dailyPrograms: Record<string, string | undefined> = {
    calm: 'Daily Calm',
    trip: 'Daily Trip',
    move: 'Daily Move',
    jay: 'Daily Jay',
} as const;

type UseVariantAwareSessionCardProps = Pick<
    ProgramContextAwareSessionCardProps,
    | 'variant'
    | 'session'
    | 'sessionIndex'
    | 'categoryTitle'
    | 'trackProps'
    | 'onPlay'
    | 'progressSource'
    | 'additionalAction'
    | 'imageIconTop'
    | 'onClick'
>;
/**
 * Do not use this hook outside of SessionCard and its dependencies.
 */
export function useVariantAwareSessionCardProps({
    variant,
    session,
    sessionIndex,
    categoryTitle: _categoryTitle,
    trackProps,
    onPlay,
    progressSource,
    additionalAction,
    onClick: customOnClick,
}: UseVariantAwareSessionCardProps) {
    const { formatMessage } = useIntl();
    const packTitle = usePackContext();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const { numSessions } = useProvidedProgramContextWithWorkflow();

    const {
        durationText,
        program,
        programPushRoute,
        isMindshiftSession,
        isClinicalSession,
        isDailySession,
    } = useVariantAgnosticSessionCardProps({
        session,
        sessionIndex,
        categoryTitle: _categoryTitle,
    });

    const buttonAriaLabel = useMemo(
        () =>
            isClinicalSession
                ? formatMessage(commonMessages['common.play_session_number'], {
                      number: sessionIndex,
                  })
                : formatMessage(commonMessages['common.play'], {
                      title: program?.title,
                  }),
        [isClinicalSession, formatMessage, sessionIndex, program?.title]
    );

    const titleTopSpacing = variant === CardVariant.Horizontal ? 1 : undefined;
    const imageForcedSize =
        variant === CardVariant.Horizontal
            ? DS_HEIGHT.HORIZONTAL_CARD_SIZE
            : undefined;
    const horizontalSx =
        variant === CardVariant.Horizontal ? styleHorizontalCard : undefined;
    const defaultContentSx = isCarousel(variant)
        ? styleCarouselContent
        : undefined;

    const { playHandler } = useSessionCardPlayHandler({
        session,
        trackProps,
        onPlay,
        progressSource,
    });

    const categoryDescription = useMemo(() => {
        if (isMindshiftSession) {
            return;
        }
        if (isDailySession) {
            return (
                program?.title ??
                (session.dailyContentType &&
                    dailyPrograms[session.dailyContentType])
            );
        }

        if (!isBanner(variant)) {
            return;
        }

        if (isClinicalSession) {
            return formatMessage(commonMessages['common.sessions'], {
                sessions: numSessions,
            });
        }

        return durationText;
    }, [
        isMindshiftSession,
        isDailySession,
        variant,
        isClinicalSession,
        durationText,
        program?.title,
        session.dailyContentType,
        formatMessage,
        numSessions,
    ]);

    const focusIcon = useMemo(() => {
        if (variant === CardVariant.Banner) {
            return;
        }

        if (programPushRoute) {
            return <ForwardFocusIcon variant={variant} />;
        }

        return <PlayFocusIcon variant={variant} />;
    }, [programPushRoute, variant]);

    const builtInOnClick = useCallback(() => {
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemClicked,
            guide: session,
            program,
            packTitle,
            trackProps,
        });

        if (variant !== CardVariant.Banner) {
            playHandler().catch((err) =>
                logger.error('Failed to play session', {}, err as Error)
            );
        }
    }, [
        trackProps,
        packTitle,
        playHandler,
        program,
        session,
        trackPackItemEvent,
        variant,
    ]);

    const additionalImageDecorations: ImageDecorationProps[] = useMemo(
        () =>
            variant === CardVariant.BannerV3 && additionalAction
                ? [
                      {
                          position: ImageDecorationPosition.TopRight,
                          isInteractive: true,
                          children: additionalAction,
                      },
                  ]
                : [],
        [variant, additionalAction]
    );

    return {
        onClick: customOnClick ?? builtInOnClick,
        defaultContentSx,
        horizontalSx,
        imageForcedSize,
        focusIcon,
        categoryDescription,
        titleTopSpacing,
        additionalImageDecorations,
        buttonAriaLabel,
    };
}
